import { useState, useEffect, useRef, useCallback } from "react";
import { useAgent } from "agents/react";
import { useAgentChat } from "@cloudflare/ai-chat/react";
import { useAuth } from "../contexts/AuthContext";
import { apiFetch } from "../lib/api";
import ApprovalDialog, { type QueueItem } from "../components/ApprovalDialog";
import KnowledgeGraph, { type GraphNode, type GraphLink } from "../components/KnowledgeGraph";
import NodeDetailPanel from "../components/NodeDetailPanel";
import ChatPanel from "../components/ChatPanel";
import ChatInput from "../components/ChatInput";
import SearchModal from "../components/SearchModal";
import UserInfoBar from "../components/UserInfoBar";
import { Search, ListTodo } from "lucide-react";

interface KnowledgeNode {
	nid: string;
	name: string;
	properties?: string;
}

interface KnowledgeRelation {
	rid: string;
	from_nid: string;
	to_nid: string;
	name: string;
	properties?: string;
}

const NODE_COLORS = [
	"#3b82f6", "#8b5cf6", "#06b6d4", "#10b981", "#f59e0b",
	"#ef4444", "#ec4899", "#6366f1", "#14b8a6", "#f97316",
];

interface ApprovalState {
	queue: QueueItem[];
	resolve: (result: unknown) => void;
}

export default function HomePage() {
	const { accountName, accountId, logout } = useAuth();

	// ── Graph state ──
	const graphRef = useRef<any>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const [graphData, setGraphData] = useState<{ nodes: GraphNode[]; links: GraphLink[] }>({ nodes: [], links: [] });
	const [graphLoading, setGraphLoading] = useState(true);
	const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
	const [dimensions, setDimensions] = useState({ width: 800, height: 600 });

	// ── Chat state ──
	const [chatOpen, setChatOpen] = useState(false);
	const [inputValue, setInputValue] = useState("");
	const [approvalState, setApprovalState] = useState<ApprovalState | null>(null);
	const [queueOpen, setQueueOpen] = useState(false);
	const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
	const [queueLoading, setQueueLoading] = useState(false);
	const [approving, setApproving] = useState(false);
	const messagesEndRef = useRef<HTMLDivElement>(null);

	// ── Search state ──
	const [searchOpen, setSearchOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [searchResults, setSearchResults] = useState<KnowledgeNode[]>([]);
	const [searchLoading, setSearchLoading] = useState(false);
	const [searched, setSearched] = useState(false);

	// ── Agent / Chat ──
	const agent = useAgent({ 
		agent: "ChatAgent",
		name: accountId!,
		onStateUpdate: useCallback((state: unknown) => {
			console.log("New state:", state);
		},[]),
		onMessage: useCallback((message: unknown) => {
			console.log("New message:", message);
		},[])
	});
	const { messages, sendMessage, setMessages, clearHistory, stop, status } = useAgentChat({
		agent,
		body: () => ({
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			language: navigator.language,
			device: navigator.userAgent,
			screenSize: `${window.innerWidth}x${window.innerHeight}`,
			timestamp: Date.now(),
		}),
		onToolCall: async ({ toolCall, addToolOutput }) => {
			if (toolCall.toolName === "requestApproval") {
				try {
					const response = await apiFetch<{ queue: QueueItem[] }>(
						`/api/userspace/${accountId}/queue`,
					);
					const queue = response.queue ?? [];
					if (queue.length === 0) {
						addToolOutput({
							toolCallId: toolCall.toolCallId,
							output: { approved: false, message: "No pending modifications to approve." },
						});
						return;
					}
					setQueueItems(queue);
					const result = await new Promise<unknown>((resolve) => {
						setApprovalState({ queue, resolve });
					});
					addToolOutput({ toolCallId: toolCall.toolCallId, output: result });
				} catch {
					addToolOutput({
						toolCallId: toolCall.toolCallId,
						output: { approved: false, error: "Failed to fetch modification queue" },
					});
				}
				setApprovalState(null);
				return;
			} else if (toolCall.toolName === "getUserContext") {
				addToolOutput({
					toolCallId: toolCall.toolCallId,
					output: {
						timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
						time: new Date().toISOString(),
						device: navigator.userAgent,
						screenSize: `${window.innerWidth}x${window.innerHeight}`,
					},
				});
			}
		},
	});

	const isStreaming = status === "streaming" || status === "submitted";

	const fetchQueue = useCallback(async () => {
		if (!accountId) return [] as QueueItem[];
		setQueueLoading(true);
		try {
			const response = await apiFetch<{ queue: QueueItem[] }>(
				`/api/userspace/${accountId}/queue`,
			);
			const nextQueue = response.queue ?? [];
			setQueueItems(nextQueue);
			setApprovalState((prev) => (prev ? { ...prev, queue: nextQueue } : null));
			return nextQueue;
		} catch {
			return [] as QueueItem[];
		} finally {
			setQueueLoading(false);
		}
	}, [accountId]);

	// ── Graph data loading ──
	const fetchGraphData = useCallback(async () => {
		if (!accountId) return;
		setGraphLoading(true);
		try {
			const data = await apiFetch<{ nodes: KnowledgeNode[]; relations: KnowledgeRelation[] }>(
				`/api/userspace/${accountId}/graph`,
			);
			const typeColorMap = new Map<string, string>();
			let colorIdx = 0;
			const nodes: GraphNode[] = (data.nodes ?? []).map((n) => {
				let props: Record<string, unknown> = {};
				try { props = n.properties ? JSON.parse(n.properties) : {}; } catch { /* ignore */ }
				const type = (props.type as string) || "default";
				if (!typeColorMap.has(type)) {
					typeColorMap.set(type, NODE_COLORS[colorIdx % NODE_COLORS.length]);
					colorIdx++;
				}
				return { id: n.nid, name: n.name, properties: props, val: 1, color: typeColorMap.get(type) };
			});
			const nodeIds = new Set(nodes.map((n) => n.id));
			const links: GraphLink[] = (data.relations ?? [])
				.filter((r) => nodeIds.has(r.from_nid) && nodeIds.has(r.to_nid))
				.map((r) => ({ source: r.from_nid, target: r.to_nid, name: r.name, rid: r.rid }));
			setGraphData({ nodes, links });
		} catch { /* ignore */ } finally {
			setGraphLoading(false);
		}
	}, [accountId]);

	useEffect(() => { fetchGraphData(); }, [fetchGraphData]);

	useEffect(() => {
		if (!accountId) return;
		fetchQueue();
	}, [accountId, fetchQueue]);

	// ── Dimensions ──
	useEffect(() => {
		const update = () => {
			if (containerRef.current) {
				setDimensions({ width: containerRef.current.clientWidth, height: containerRef.current.clientHeight });
			}
		};
		update();
		window.addEventListener("resize", update);
		return () => window.removeEventListener("resize", update);
	}, []);

	// ── Scroll chat ──
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages]);

	// ── Graph interactions ──
	const handleNodeClick = useCallback((node: any) => {
		setSelectedNode(node as GraphNode);
		if (graphRef.current) {
			graphRef.current.centerAt(node.x, node.y, 500);
			graphRef.current.zoom(3, 500);
		}
	}, []);

	// ── Chat actions ──
	const handleSend = () => {
		const text = inputValue.trim();
		if (!text || isStreaming) return;
		sendMessage({ text });
		setInputValue("");
		setChatOpen(true);
	};

	const handleApprove = async (selectedQids: string[]) => {
		if (approving) return;
		setApproving(true);
		try {
			const result = await apiFetch<{ results: unknown[] }>(
				`/api/userspace/${accountId}/queue/execute`,
				{ method: "POST", body: JSON.stringify({ qids: selectedQids }) },
			);
			await Promise.all([fetchQueue(), fetchGraphData()]);
			if (approvalState) {
				approvalState.resolve({
					approved: true,
					executedCount: selectedQids.length,
					results: result.results,
					message: `${selectedQids.length} modification(s) applied successfully.`,
				});
			}
		} catch {
			if (approvalState) {
				approvalState.resolve({ approved: false, error: "Failed to execute modifications" });
			}
		} finally {
			setApproving(false);
		}
	};

	const handleReject = async () => {
		if (approving) return;
		try {
			await apiFetch(`/api/userspace/${accountId}/queue`, { method: "DELETE" });
			await fetchQueue();
		} catch { /* ignore */ }
		if (approvalState) {
			approvalState.resolve({ approved: false, message: "User rejected all pending modifications." });
		}
	};

	const handleRemoveQueueItem = async (qid: string) => {
		if (approving) return;
		try {
			await apiFetch(`/api/userspace/${accountId}/queue/${qid}`, { method: "DELETE" });
			setQueueItems((prev) => prev.filter((item) => item.qid !== qid));
			setApprovalState((prev) =>
				prev ? { ...prev, queue: prev.queue.filter((item) => item.qid !== qid) } : null,
			);
			await fetchQueue();
		} catch { /* ignore */ }
	};

	const handleUpdateQueueItem = async (qid: string, updates: { args: string; description: string }) => {
		if (approving) return;
		await apiFetch(`/api/userspace/${accountId}/queue/${qid}`, {
			method: "PUT",
			body: JSON.stringify(updates),
		});
		await Promise.all([fetchQueue(), fetchGraphData()]);
	};

	// ── Search handler ──
	const handleSearch = async () => {
		if (!accountId) return;
		setSearchLoading(true);
		setSearched(true);
		try {
			const params = new URLSearchParams();
			if (searchQuery.trim()) params.set("q", searchQuery.trim());
			params.set("limit", "50");
			const data = await apiFetch<{ nodes: KnowledgeNode[] }>(
				`/api/userspace/${accountId}/nodes?${params.toString()}`,
			);
			setSearchResults(data.nodes ?? []);
		} catch { /* ignore */ } finally {
			setSearchLoading(false);
		}
	};

	const focusNodeOnGraph = (nid: string) => {
		const node = graphData.nodes.find((n) => n.id === nid);
		if (node && graphRef.current) {
			setSelectedNode(node);
			graphRef.current.centerAt((node as any).x, (node as any).y, 500);
			graphRef.current.zoom(3, 500);
			setSearchOpen(false);
		}
	};

	return (
		<div className="h-screen w-screen bg-slate-900 overflow-hidden relative">
			<KnowledgeGraph
				graphRef={graphRef}
				containerRef={containerRef}
				graphData={graphData}
				graphLoading={graphLoading}
				dimensions={dimensions}
				selectedNode={selectedNode}
				onNodeClick={handleNodeClick}
				onRefresh={fetchGraphData}
			/>

			{selectedNode && (
				<NodeDetailPanel
					node={selectedNode}
					links={graphData.links}
					nodes={graphData.nodes}
					onClose={() => setSelectedNode(null)}
				/>
			)}

			<UserInfoBar
				accountName={accountName}
				nodeCount={graphData.nodes.length}
				linkCount={graphData.links.length}
				queueCount={queueItems.length}
				onLogout={logout}
			/>

			<div className="absolute bottom-4 right-4 z-30 flex items-center gap-2">
				<button
					onClick={async () => {
						await fetchQueue();
						setQueueOpen(true);
					}}
					className="relative flex items-center gap-2 px-4 py-2.5 bg-slate-800/80 backdrop-blur-xl border border-slate-700/50 rounded-xl shadow-lg text-slate-300 hover:text-white hover:border-slate-500 transition-all"
				>
					<ListTodo className="w-4 h-4" />
					<span className="text-sm font-medium">Queue</span>
					<span className="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-blue-600 text-white text-xs font-semibold">
						{queueLoading ? "..." : queueItems.length}
					</span>
				</button>
				<button
					onClick={() => setSearchOpen(true)}
					className="flex items-center gap-2 px-4 py-2.5 bg-slate-800/80 backdrop-blur-xl border border-slate-700/50 rounded-xl shadow-lg text-slate-300 hover:text-white hover:border-slate-500 transition-all"
				>
					<Search className="w-4 h-4" />
					<span className="text-sm font-medium">Search</span>
				</button>
			</div>

			<ChatInput
				inputValue={inputValue}
				onInputChange={setInputValue}
				onSend={handleSend}
				isStreaming={isStreaming}
				onStop={stop}
				onOpenChat={() => setChatOpen(true)}
				hasMessages={messages.length > 0}
				chatOpen={chatOpen}
			/>

			{chatOpen && (
				<ChatPanel
					messages={messages as any}
					isStreaming={isStreaming}
					onClearHistory={clearHistory}
					onClose={() => setChatOpen(false)}
					onDeleteMessage={(id) => setMessages((prev) => prev.filter((m) => m.id !== id))}
					messagesEndRef={messagesEndRef}
				/>
			)}

			<SearchModal
				open={searchOpen}
				onClose={() => setSearchOpen(false)}
				searchQuery={searchQuery}
				onQueryChange={setSearchQuery}
				onSearch={handleSearch}
				results={searchResults}
				loading={searchLoading}
				searched={searched}
				onFocusNode={focusNodeOnGraph}
			/>

			{approvalState && (
				<ApprovalDialog
					queue={approvalState.queue}
					onApprove={handleApprove}
					onReject={handleReject}
					onRemoveItem={handleRemoveQueueItem}
					onUpdateItem={handleUpdateQueueItem}
					approving={approving}
				/>
			)}

			{queueOpen && (
				<ApprovalDialog
					queue={queueItems}
					onApprove={handleApprove}
					onReject={handleReject}
					onRemoveItem={handleRemoveQueueItem}
					onUpdateItem={handleUpdateQueueItem}
					onClose={() => setQueueOpen(false)}
					title="Queue Task Manager"
					subtitle="Open queue tasks, edit or remove before execution"
					approveLabel="Execute Selected"
					approving={approving}
				/>
			)}
		</div>
	);
}
